import { cueTypeById } from './plannerCues';

/**
 * The display copy for the planner's turn-by-turn feature.
 *
 * Kept apart from `plannerCues` because that file is the cross-platform data contract — its
 * English `label`s match `PlannerCueType` on Android and iOS — while this is what a person
 * reads in their own language.
 *
 * The translator is passed in rather than imported so this stays pure. Every key is a
 * literal `t('…')` call on purpose: the client bundle only ships keys it can find written out
 * in app/javascript, so a key assembled from the id would arrive in the browser as the key.
 */
export type Translate = (key: string, named?: Record<string, unknown>) => string;

/** What the cue-type picker shows for `id`, in the reader's language. */
export function cueTypeLabel(id: string, t: Translate): string {
  switch (id) {
    case 'note':
      return t('plan_trail.custom_cue_dialog.cue_types.note');
    case 'left':
      return t('plan_trail.custom_cue_dialog.cue_types.left');
    case 'right':
      return t('plan_trail.custom_cue_dialog.cue_types.right');
    case 'straight':
      return t('plan_trail.custom_cue_dialog.cue_types.straight');
    case 'slight_left':
      return t('plan_trail.custom_cue_dialog.cue_types.slight_left');
    case 'slight_right':
      return t('plan_trail.custom_cue_dialog.cue_types.slight_right');
    case 'sharp_left':
      return t('plan_trail.custom_cue_dialog.cue_types.sharp_left');
    case 'sharp_right':
      return t('plan_trail.custom_cue_dialog.cue_types.sharp_right');
    case 'keep_left':
      return t('plan_trail.custom_cue_dialog.cue_types.keep_left');
    case 'keep_right':
      return t('plan_trail.custom_cue_dialog.cue_types.keep_right');
    case 'uturn':
      return t('plan_trail.custom_cue_dialog.cue_types.uturn');
    default:
      // A type added to the contract before its copy — English beats a blank option.
      return cueTypeById(id)?.label ?? '';
  }
}

/** How many of the list's rows are steps: a straight line segment's row is not one. */
export function turnStepCount(rows: { straightLineMeters?: number }[]): number {
  return rows.filter((row) => row.straightLineMeters === undefined).length;
}

/** The subtitle under "Turn-by-turn": how many rows the list has. */
export function stepCountLabel(count: number, t: Translate): string {
  return count === 1
    ? t('plan_trail.actions.turn_instructions_step_count_one', { count })
    : t('plan_trail.actions.turn_instructions_step_count_other', { count });
}
